import { Trans, useLingui } from '@lingui/react/macro';
import { User } from '@remarkable/ark-icons';
import { Divider, Typography } from '@remarkable/ark-web';
import type { ChangeEvent } from 'react';
import { useState } from 'react';
import { type FleetUser } from 'src/api/endpoints/cloudEnterprise.types';
import { useDisableMemberMdm } from 'src/api/queries/enterpriseAdmin';
import { Button, Input, Modal } from 'src/components';
import { CopyButton } from 'src/components/CopyButton';

/**
 * The instructions an admin hands over to the user whose MDM is being
 * deactivated. Rendered in the dialog and copied to the clipboard from the
 * same source, so the two can never drift apart.
 */
function useMdmTransferInstructions() {
  const { t } = useLingui();

  const intro = t`Follow instructions to transfer files to another account:`;

  const steps = [
    t`Make sure the device is done syncing and you are connected to WiFi.`,
    t`Factory reset the device (Settings → click on the account → Factory reset).`,
    t`Restart the device and connect to WiFi. Pair your device to the same account as earlier.`,
    t`When it is paired, wait for it to finish syncing.`,
    t`Then unpair the device (Settings → click on the account → Unpair).`,
    t`Log in with the new account on my.reMarkable.com and pair the device.`,
    t`Wait for the device to sync.`,
    t`The data is now transferred to the new account.`,
    t`Let your IT admin know when it is done so the old account can be deleted.`,
  ];

  const asText = [
    intro,
    '',
    ...steps.map((step, index) => `${index + 1}. ${step}`),
  ].join('\n');

  return { intro, steps, asText };
}

export function ConfirmMdmDeactivationModal({
  member,
  closeModal,
  handleDeactivation,
}: {
  member: FleetUser;
  closeModal: () => void;
  handleDeactivation: () => void;
}) {
  const { t } = useLingui();
  const instructions = useMdmTransferInstructions();
  const disableMemberMdm = useDisableMemberMdm();
  const [confirmInput, setConfirmInput] = useState('');
  const isConfirmed = confirmInput.toLowerCase() === 'deactivate';

  const handleConfirmClick = () => {
    setConfirmInput('');
    closeModal();
    handleDeactivation();
  };

  return (
    <Modal.Content title={t`Deactivate MDM for user?`}>
      <div className="flex flex-col gap-y-24">
        <Typography
          variant="body-lg"
          as="div"
          className="flex flex-wrap items-center justify-center gap-4"
        >
          <User size={24} className="mr-4 inline" />
          <b>{member.name}</b> <span>({member.email})</span>
        </Typography>
        <Divider />
        <div className="flex flex-col items-start gap-24 text-left">
          <Typography variant="body-md" as="p" className="text-left">
            <Trans>
              By disabling Mobile Device Management (MDM), you are removing
              security protocols from the user's devices. This grants the user
              control to disable security features (like screen locks and pin
              code) and allows them to transfer their data to external,
              non-company accounts.
            </Trans>
          </Typography>
          <Typography variant="body-md" as="p" className="text-left">
            <Trans>
              If you are doing this to make a user transfer the data to a new
              account, you can share these instructions to the user:
            </Trans>
          </Typography>
          <Divider className="w-full" />
          <Typography variant="body-md" as="div" className="text-left">
            {instructions.intro}
            <ol className="mt-16 ml-16 flex list-outside list-decimal flex-col items-start gap-8 text-left">
              {instructions.steps.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ol>
          </Typography>
          <CopyButton value={instructions.asText} size="small">
            <Trans>Copy user instructions</Trans>
          </CopyButton>
        </div>
        <Input
          label={
            <Typography variant="body-md" className="text-left">
              <Trans>
                Please type <b>Deactivate</b> to confirm
              </Trans>
            </Typography>
          }
          value={confirmInput}
          onChange={(e: ChangeEvent<HTMLInputElement>) =>
            setConfirmInput(e.currentTarget.value)
          }
          name="confirm-mdm-deactivation"
          data-cy="confirm-mdm-deactivation"
          className="w-full"
          required
          maxLength={150}
          autoComplete="off"
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.stopPropagation();
              if (isConfirmed) {
                handleConfirmClick();
              }
            }
          }}
        />
        <div className="flex justify-center gap-16">
          <Modal.Close asChild>
            <Button variant="secondary">
              <Trans>Cancel</Trans>
            </Button>
          </Modal.Close>

          <Button
            variant="primary"
            loading={disableMemberMdm.isPending}
            disabled={!isConfirmed}
            onClick={handleConfirmClick}
          >
            <Trans>Confirm</Trans>
          </Button>
        </div>
      </div>
    </Modal.Content>
  );
}
